import { Injectable, ApplicationRef } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { BehaviorSubject, filter, first, interval } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from 'environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AppUpdateService {

  readonly updateReady$ = new BehaviorSubject<boolean>(false);
  readonly currentBuildLabel$  = new BehaviorSubject<string | null>(null);

  constructor(
    private swUpdate: SwUpdate,
    private snackBar: MatSnackBar,
    private appRef: ApplicationRef
  ) {}

  init(): void {
    if (!this.swUpdate.isEnabled) {
      console.log('Service Worker no está habilitado');
      return;
    }

    // Verificar actualizaciones cada 6 horas
    this.checkForUpdates();

    // Escuchar cuando hay una nueva versión disponible
    this.swUpdate.versionUpdates
      .pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY')
      )
      .subscribe((evt) => {
        this.updateReady$.next(true);
        console.log('Nueva versión disponible:', evt.latestVersion.hash);
        this.promptUser();
      });

    // Log de errores
    this.swUpdate.unrecoverable.subscribe((event) => {
      console.error('Error irrecuperable en Service Worker:', event.reason);
      this.snackBar.open(
        'Error en la aplicación. Por favor, recarga la página.',
        'Recargar',
        { duration: 0 }
      ).onAction().subscribe(() => {
        window.location.reload();
      });
    });
  }

  private checkForUpdates(): void {
    // Esperar a que la app esté estable
    this.appRef.isStable
      .pipe(first((isStable) => isStable))
      .subscribe(() => {
        console.log('Aplicación estable, iniciando verificación de actualizaciones');
        this.currentBuildLabel$.next(`${environment.version} (${environment.buildHash})`);
        // Verificar cada 6 horas
        interval(6 * 60 * 60 * 1000).subscribe(() => {
          this.swUpdate.checkForUpdate().then(() => {
            console.log('Verificación de actualización completada');
          });
        });
      });
  }

  private promptUser(): void {
    const snackBarRef = this.snackBar.open(
      `Nueva versión disponible ${environment.version} (${environment.buildHash})`,
      'Actualizar',
      {
        duration: 0, // No se cierra automáticamente
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: ['update-snackbar']
      }
    );

    snackBarRef.onAction().subscribe(() => {
      this.activateUpdate();
    });
  }

  private activateUpdate(): void {
    this.swUpdate.activateUpdate().then(() => {
      console.log('Activando nueva versión...');
      window.location.reload();
    });
  }

  // Método manual para forzar verificación
  checkForUpdate(): void {
    this.swUpdate.checkForUpdate().then((updateFound) => {
      if (updateFound) {
        console.log('Actualización encontrada');
      } else {
        console.log('Ya estás en la última versión');
        this.snackBar.open('Ya estás usando la última versión', 'OK', {
          duration: 3000
        });
      }
    });
  }
}
